/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import { saveAs } from 'file-saver';

/** Common Components */
import Buttons from "../../../../../../components/ui/button/Buttons";
import CardSection from "../../../../../../components/ui/card/CardSection";
import CustomCenterModel from "../../../../../../components/ui/customCenterModel/CustomCenterModel";
import formatDate from "../../../../../../components/finalMolGrid/libs/formatDate";
import MolGrid, { Column } from "@moltech/molui/molgrid";
import { AppIcons } from "../../../../../../data/appIcons";

/** Services */
import ToastService from "../../../../../../services/toastService/ToastService";
import SwalAlert from "../../../../../../services/swalService/SwalService";

/** Utilities */
import EditScopes from "./features/EditContractScopes";
import { ContractContext } from "../../../../../../utils/context/ContractContext";
import AvailableContractScopes from "./AvailableContractScopes";
import ImportContractScopes from "./features/ImportContractScopes";

/** RTK Query */
import { useContractDepartmentUpdateCostFromScopesMutation, useDeleteContractScopeMutation, useExportContractScopesMutation, useGetAllContractScopesMutation } from "../../../../../../app/services/contractAPI";

const Scopes = () => {

  const { contractData, contractId } = useContext(ContractContext);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [contractScopes, setContractScopes] = useState([]);
  const [reloadAvailable, setReloadAvailable] = useState(null);
  const [rowData, setRowData] = useState(null);
  const currentDate = formatDate(new Date(), "MM-DD-YYYY");
  const [isImportModelOpen, setIsImportModelOpen] = useState(false);
  const {confirm} = SwalAlert();

  const [getAllContractScopes, {
    isFetching: isAllContractScopesFetching,
    isSuccess: isAllContractScopesFetched,
    data: AllContractScopesData,
  }] = useGetAllContractScopesMutation();

  const [deleteScope, {
    isSuccess: isDeleteScopeFetching,
    data: deleteScopeData
  }] = useDeleteContractScopeMutation();

  const [exportContractScopes, {
    isLoading: isExporting,
    isSuccess: isAllExported,
    data: exportAllData
  }] = useExportContractScopesMutation();

  const [contractDepartmentUpdateCostFromScopes] = useContractDepartmentUpdateCostFromScopesMutation();
  
  useEffect(() => {
    if (!isAllContractScopesFetching && isAllContractScopesFetched && AllContractScopesData) {
      setContractScopes(AllContractScopesData);
    }
  }, [isAllContractScopesFetching, isAllContractScopesFetched, AllContractScopesData])

  useEffect(() => {
    if (isDeleteScopeFetching && deleteScopeData) {
      if (deleteScopeData) {
        ToastService.success(deleteScopeData?.errorMessage);
        getAllContractScopes(req);
        contractDepartmentUpdateCostFromScopes({ plContractKey: Number(contractId), plDepartmentKey: rowData?.lDepartmentKey });
        if (reloadAvailable) {
          reloadAvailable();
        }
      }
      else {
        ToastService.error("Something went wrong");
      }
    }
  }, [isDeleteScopeFetching, deleteScopeData, getAllContractScopes]);

  useEffect(() => {
    if (!isExporting && isAllExported && exportAllData) {
      const blob = new Blob([exportAllData]);
      saveAs(blob, `Contract Scopes ${currentDate}.xlsx`);
    }
  }, [isExporting, isAllExported, exportAllData]);

  const terminationDate = contractData?.dtDateTermination;
  const today = new Date();

  const dtAsOfDate = terminationDate === null ? today : new Date(terminationDate) > today ? today : new Date(terminationDate);
  const req = {
    plContractKey: contractId,
    pbIncludeInactive: true,
    pdtAsOfDate: dtAsOfDate,
    plScopeKey: 0,
    pbExcludeCPO: false,
    pbShowFutureScopes: true
  }

  useEffect(() => {
    if (contractId && contractData) {
      getAllContractScopes(req);
    }
  }, [contractData, contractId]);

 const handleExportClick = () => {
  confirm(
    "Click 'Yes' to include ALL contract scopes, or 'No' to only include Currently-Covered scopes.",
    "",
    "Yes",
    "No",
  ).then((confirmed) => { 
    const req = {
      plContractKey: contractId,
      pbIncludeInactive: confirmed ? true : false, 
      pdtAsOfDate: dtAsOfDate,
      plScopeKey: 0,
      pbExcludeCPO: false,
      pbShowFutureScopes: true
    };
    exportContractScopes(req);
  });
};

  const headerContent = () => {
    return (
      <div className="d-flex justify-content-end gap-3">
        <Buttons
          buttonText="Extract Covered"
           onClick={handleExportClick}
          buttonTypeClassName="btn blue-btn"
          titleText="Extract Covered Scopes"
          isDisable={contractScopes.length <= 0}
        />
        <Buttons
          buttonText="Import Covered"
           onClick={()=>setIsImportModelOpen(true)}
          buttonTypeClassName="btn blue-btn"
          titleText="Import Covered Scopes"
        />
      </div>
    );
  };

  const handleCloseModel = () => {
    setIsModelOpen(false);
  };

  const handleEdit = (rowData) => {
    setIsModelOpen(true);
    setRowData(rowData);
  };

  const handleDelete = (rowData) => {
    setRowData(rowData);
    deleteScope(rowData?.lContractScopeKey)
  }

  const handleCloseImportModel = () => {
    setIsImportModelOpen(false);
  }

  const handleSuccessImport = () => {
    handleCloseImportModel();
    getAllContractScopes(req);
  }

  const getCtxMenuItems = (row) => {
    if (!row) return [];

    return [
      {
        label: "Edit",
        command: () => handleEdit(row),
      },
      {
        label: "Remove",
        // icon: 'pi pi-pen-to-square',
        command: () => handleDelete(row)
      }
    ];
  };

  return (
    <>
      <div className="row gap-4">
        <div className="scrollable-grid col-12">
          <CardSection
            cardTitle={`Covered Scopes`}
            customHeader={true}
            headerContent={headerContent()}
          >
            <MolGrid
              value={contractScopes}
              grouping
              expandableRowGroups={true}
              paginator={false}
              scrollable
              scrollHeight="flex"
              filterDisplay="row"
              sortMode="single"
              showGridlines
              resizableColumns
              ctxMenuItems={getCtxMenuItems}
            >
              <Column field="sSerialNumber" header="Serial #" bodyStyle={{ textAlign: "center" }} grouping sortable />
              <Column field="sScopeTypeDesc" header="Model" grouping sortable />
              <Column field="sClientName1" header="Client" grouping sortable />
              <Column field="sDepartmentName" header="Department" bodyStyle={{ textAlign: "Center" }} grouping sortable />
              <Column field="startDate" header="Effective Date" colType="datetime" format="mm/dd/yyyy" bodyStyle={{ textAlign: "center" }} grouping sortable />
              <Column field="endDate" header="Expiration Date" colType="datetime" format="mm/dd/yyyy" bodyStyle={{ textAlign: "center" }} grouping sortable />
              <Column field="nCost" header="Cost" colType="money" bodyStyle={{ textAlign: "right" }} grouping sortable />
            </MolGrid>
          </CardSection>
          <CustomCenterModel
            isOpen={isModelOpen}
            useCustomBg={true}
            modalTitle={"Contract Scope Edit"}
            contentClass="content-30"
            onClose={handleCloseModel}
            modalTitleIcon={AppIcons.AddIcon}
            className="add-contract-department"
          >
            <EditScopes onClose={handleCloseModel} rowData={rowData}
              reloadScopes={() => getAllContractScopes(req)}
            />
          </CustomCenterModel>
        </div>
        <AvailableContractScopes
         reloadScopes={() => getAllContractScopes(req)}
         setAvailableReload={setReloadAvailable}
         />
      </div>
      <CustomCenterModel
        isOpen={isImportModelOpen}
        useCustomBg={true}
        modalTitle={"Import Covered Scopes"}
        contentClass='content-45'
        onClose={handleCloseImportModel}
        modalTitleIcon={AppIcons.AddIcon}
      >
        <ImportContractScopes
        contractId={contractId}
          isOpen={isImportModelOpen}
          onClose={handleCloseImportModel}
          onSuccess={handleSuccessImport}
        />
      </CustomCenterModel>
    </>
  );
};
export default Scopes;
